import '@/shared/styles/relewise-product-search.css';

import pDebounce from 'p-debounce';

import {
  PREDICTIVE_SEARCH_DEBOUNCE_MS,
  relewiseTags
} from '@/shared/constants/constants';
import {
  initRelewiseFacets,
  registerRelewiseFacets
} from '@/shared/instances/relewise-facets';
import {
  handleSearchResultNull,
  initRelewiseFallback
} from '@/shared/instances/relewise-fallback-wiring';
import { initRelewiseLocalization } from '@/shared/instances/relewise-localization';
import {
  applyRelewiseMarketFilter,
  initRelewiseMarketFilter
} from '@/shared/instances/relewise-market-filter';
import { redirectRelewiseUrlToNative } from '@/shared/instances/relewise-native-results';
import { onRelewiseSearchRegistry } from '@/shared/instances/relewise-registry-hook';
import { parseResultsCount } from '@/shared/tracking/parse-results-count';
import {
  getOverlayDisplayedProducts,
  initNativeSearchResultClickTracking,
  initRelewiseOverlayResultClickTracking,
  initRelewiseSearchResultClickTracking
} from '@/shared/tracking/search-result-clicked';
import {
  getSearchQuery,
  trackResultsPageSearchUsed,
  trackSearchUsed
} from '@/shared/tracking/search-used';
import {
  deepQuerySelectorAll,
  observeDeep
} from '@/shared/tracking/shadow-dom';
import { isDesktopView } from '@/shared/utils/dom';
import { isNativeSearchView } from '@/shared/utils/search-urls';

// Raw string: the enum lives in the externally-loaded Shopify app bundle and cannot be imported here.
const RELEWISE_SEARCH_COMPLETED_EVENT =
  'relewise-ui-components:search-for-products-completed';

const suppressSearchBarAutofocus = () => {
  const isRelewiseHost = (node: EventTarget): boolean =>
    node instanceof Element &&
    node.tagName.toLowerCase().startsWith(relewiseTags.TAG_PREFIX);

  const blurIfRelewiseFocused = () => {
    let active: Element | null = document.activeElement;
    while (active?.shadowRoot?.activeElement) {
      active = active.shadowRoot.activeElement;
    }
    if (!active || active === document.body) return;

    let node: Node | null = active;
    while (node) {
      if (isRelewiseHost(node)) {
        (active as HTMLElement).blur();
        return;
      }
      const root = node.getRootNode();
      node =
        root instanceof ShadowRoot
          ? root.host
          : (node as Element).parentElement;
    }
  };

  const handleFocus = (e: FocusEvent) => {
    if (e.composedPath().some(isRelewiseHost)) {
      (e.target as HTMLElement).blur();
    }
  };

  document.addEventListener('focus', handleFocus, true);

  const timers: ReturnType<typeof setTimeout>[] = [];

  const cleanup = () => {
    timers.forEach(clearTimeout);
    document.removeEventListener('focus', handleFocus, true);
    document.removeEventListener('pointerdown', cleanup);
    document.removeEventListener('keydown', cleanup);
  };

  document.addEventListener('pointerdown', cleanup, { once: true });
  document.addEventListener('keydown', cleanup, { once: true });

  timers.push(
    setTimeout(blurIfRelewiseFocused, 1000),
    setTimeout(blurIfRelewiseFocused, 2200),
    setTimeout(cleanup, 4000)
  );
};

const isRelewiseSearchEnabled = (): boolean =>
  Boolean(window.Hobbii?.flags?.enableRelewiseSearch);

export const initSearchEngine = (): boolean => {
  if (!isRelewiseSearchEnabled()) {
    if (redirectRelewiseUrlToNative()) {
      return false;
    }
    initNativeResultsPageTracking();
    return false;
  }

  // Runs first so the labels apply before Relewise's components render for
  // the first time.
  initRelewiseLocalization();
  suppressSearchBarAutofocus();

  // The overlay is injected at runtime by the Relewise app, often after this
  // runs, so a one-shot querySelector would miss it.
  attachOverlayTrackingWhenPresent();
  if (isNativeSearchView()) {
    initNativeResultsPageTracking();
  } else {
    initRelewiseResultsPageTracking();
  }
  return true;
};

let overlayTrackingAttached = false;
let lastPredictionOverlay: HTMLElement | null = null;
let removePredictionResizeListener: (() => void) | null = null;
let activeOverlayObserver: MutationObserver | null = null;

/** Test-only: reset attach/prediction state so each spec starts clean. */
export const __resetOverlayTrackingForTests = () => {
  overlayTrackingAttached = false;
  lastPredictionOverlay = null;
  removePredictionResizeListener?.();
  removePredictionResizeListener = null;
  activeOverlayObserver?.disconnect();
  activeOverlayObserver = null;
};

// Relewise can remove and re-inject the overlay element, so the mobile
// prediction cap is re-applied to every fresh element — unlike click tracking
// below, which intentionally binds only once per page.
const applyPredictionLimitForOverlay = (overlay: HTMLElement) => {
  if (overlay === lastPredictionOverlay) {
    return;
  }
  lastPredictionOverlay = overlay;
  removePredictionResizeListener?.();

  const setPredictionCount = (isDesktop: boolean) =>
    overlay.setAttribute(
      'number-of-search-term-predictions',
      isDesktop ? '3' : '1'
    );

  let lastDesktopState = isDesktopView();
  setPredictionCount(lastDesktopState);
  const onResize = () => {
    const nowDesktop = isDesktopView();
    if (nowDesktop !== lastDesktopState) {
      lastDesktopState = nowDesktop;
      setPredictionCount(nowDesktop);
    }
  };
  window.addEventListener('resize', onResize);
  removePredictionResizeListener = () =>
    window.removeEventListener('resize', onResize);
};

export const attachOverlayTrackingWhenPresent = () => {
  const tryAttach = () => {
    const overlay = document.querySelector<HTMLElement>(
      relewiseTags.OVERLAY_TAG
    );
    if (!overlay) {
      return;
    }
    applyPredictionLimitForOverlay(overlay);
    if (!overlayTrackingAttached) {
      overlayTrackingAttached = true;
      initRelewiseOverlayTracking(overlay);
    }
  };

  // Already present.
  tryAttach();

  // A prior call's observer (if any) is superseded by this one.
  activeOverlayObserver?.disconnect();

  // Kept observing indefinitely (never disconnected on a successful attach)
  // so a later re-injected overlay still gets the prediction cap re-applied.
  const observer = new MutationObserver(tryAttach);
  observer.observe(document.body, { childList: true, subtree: true });
  activeOverlayObserver = observer;
};

const initNativeResultsPageTracking = () => {
  const resultsElement = document.querySelector<HTMLElement>(
    '#search-product-count-desktop'
  );
  initNativeSearchResultClickTracking(resultsElement);
  trackResultsPageSearchUsed(resultsElement, 'native');
};

const isSearchResultNull = (): boolean =>
  document.querySelector<RelewiseProductSearchInstance>(relewiseTags.SEARCH_TAG)
    ?.searchResult === null;

export const initRelewiseResultsPageTracking = (): (() => void) => {
  initRelewiseSearchResultClickTracking();

  let lastFiredTerm: string | null = null;

  // Dedupe on the URL term: facet changes and load-more re-renders re-dispatch
  // the completion event with the same URL term; only a genuine new search
  // rewrites it. We do NOT reset on input clear — unlike the overlay arm which
  // has no URL term, re-running the same URL term is not a real second search.
  const onSearchCompleted = () => {
    if (isSearchResultNull()) {
      handleSearchResultNull();
      return;
    }

    const term = getSearchQuery();
    if (term === lastFiredTerm) {
      return;
    }
    lastFiredTerm = term;

    trackSearchUsed({
      engine: 'relewise',
      surface: 'results_page',
      query: term,
      resultsCount: getRelewiseResultsPageCount()
    });
  };
  window.addEventListener(RELEWISE_SEARCH_COMPLETED_EVENT, onSearchCompleted);

  // Bootstrap: if Relewise already fired its completion event before this
  // listener was attached, fire once immediately.
  const search = document.querySelector<RelewiseProductSearchInstance>(
    relewiseTags.SEARCH_TAG
  );
  if (
    search?.searchResult?.hits != null ||
    (Array.isArray(search?.products) && search.products.length > 0)
  ) {
    onSearchCompleted();
  }

  return () => {
    window.removeEventListener(
      RELEWISE_SEARCH_COMPLETED_EVENT,
      onSearchCompleted
    );
  };
};

/** Local shape of the `relewise-product-search` instance state. */
type RelewiseProductSearchInstance = HTMLElement & {
  products?: unknown[];
  searchResult?: { hits?: number } | null;
};

// Instance state (populated before the completion event fires, unlike the tile DOM).
// No tile-count fallback: the responsive grid renders tiles twice, so a DOM count is
// doubled. null is correct here; a doubled number is actively wrong.
const getRelewiseResultsPageCount = (): number | null => {
  const search = document.querySelector<RelewiseProductSearchInstance>(
    relewiseTags.SEARCH_TAG
  );
  if (!search) {
    return null;
  }

  const hits = search.searchResult?.hits;
  if (typeof hits === 'number') {
    return hits;
  }

  if (Array.isArray(search.products) && search.products.length > 0) {
    return search.products.length;
  }

  // Last resort: instance fields absent (future version/minification).
  const [countEl] = deepQuerySelectorAll(search, '.rw-results-text');
  const parsed = parseResultsCount(countEl ?? null);
  if (parsed != null) {
    return parsed;
  }

  return null;
};

const getRelewiseOverlayTerm = (overlay: HTMLElement): string | null => {
  // (1) The Relewise search form's own light-DOM input - the visible field the
  // user types into when Relewise is the active engine.
  const relewiseInputValue = document
    .querySelector<HTMLInputElement>('.js-relewise-search-form input[name="q"]')
    ?.value?.trim();
  if (relewiseInputValue) {
    return relewiseInputValue;
  }

  // (2) Shadow-pierced overlay input - the overlay may host its own (nested
  // shadow) input.
  const overlayInputValue = deepQuerySelectorAll(overlay, 'input')
    .map((el) => (el as HTMLInputElement).value?.trim())
    .find((value) => Boolean(value));
  if (overlayInputValue) {
    return overlayInputValue;
  }

  // (3) Any other visible `input[name="q"]`; requiring visibility so a stale,
  // display:none/hidden input can never win.
  const visibleInputValue = Array.from(
    document.querySelectorAll<HTMLInputElement>('input[name="q"]')
  )
    .filter((el) => !el.hidden && el.offsetParent !== null)
    .map((el) => el.value?.trim())
    .find((value) => Boolean(value));
  if (visibleInputValue) {
    return visibleInputValue;
  }

  // (4) URL term.
  return getSearchQuery();
};

// Same resolution order as getRelewiseOverlayTerm but WITHOUT the URL fallback.
// Used to detect "input is empty" — the URL may still carry a stale term after the
// user clears the field, so the URL fallback would give a false-positive live value.
const getLiveOverlayInputValue = (overlay: HTMLElement): string => {
  const relewiseInputValue = document
    .querySelector<HTMLInputElement>('.js-relewise-search-form input[name="q"]')
    ?.value?.trim();
  if (relewiseInputValue) {
    return relewiseInputValue;
  }

  const overlayInputValue = deepQuerySelectorAll(overlay, 'input')
    .map((el) => (el as HTMLInputElement).value?.trim())
    .find((value) => Boolean(value));
  if (overlayInputValue) {
    return overlayInputValue;
  }

  const visibleInputValue = Array.from(
    document.querySelectorAll<HTMLInputElement>('input[name="q"]')
  )
    .filter((el) => !el.hidden && el.offsetParent !== null)
    .map((el) => el.value?.trim())
    .find((value) => Boolean(value));

  return visibleInputValue ?? '';
};

export const initRelewiseOverlayTracking = (overlay: HTMLElement) => {
  initRelewiseOverlayResultClickTracking(overlay, () =>
    getRelewiseOverlayTerm(overlay)
  );

  let lastFiredTerm: string | null = null;
  let lastFireWasConfident = false;

  // Reset dedupe on clear so "cotton" → clear → "cotton" fires again.
  // The overlay has no URL term to dedupe against, so a clear is the only signal
  // that the same term typed again is a genuine second search.
  // Capture at document level: the user may type in the light-DOM Relewise form
  // OR inside the overlay's nested shadow tree; a composed input event covers both.
  const onInputForReset = () => {
    if (getLiveOverlayInputValue(overlay) === '') {
      lastFiredTerm = null;
      lastFireWasConfident = false;
    }
  };
  document.addEventListener('input', onInputForReset, true);

  const fireIfNewResults = () => {
    const renderedTiles = deepQuerySelectorAll(
      overlay,
      relewiseTags.OVERLAY_PRODUCT_TAG
    );
    if (renderedTiles.length === 0) {
      return;
    }
    // Stale tiles can persist after the user clears the input; bail to avoid
    // emitting search_used with the URL-term fallback when there is no live query.
    if (getLiveOverlayInputValue(overlay) === '') {
      return;
    }
    const term = getRelewiseOverlayTerm(overlay);
    if (!term) {
      return;
    }

    // Instance state is the same source resolveOverlayPositionCount uses, so
    // search_used and search_result_clicked always report identical results_count.
    const instanceProducts = getOverlayDisplayedProducts(overlay);
    // An empty product list while tiles are rendered means the results array
    // initialised before products hydrated into it — treat as not-confident so
    // a later re-fire can correct the count. null = no array yet (also not confident).
    const confident = instanceProducts !== null && instanceProducts.length > 0;
    const resultsCount = confident
      ? instanceProducts.length
      : renderedTiles.length;

    // A non-confident fire fell back to the DOM tile set, which can be stale
    // (observer fired during a loading-state mutation before instance state
    // hydrated). Allow one re-fire for the same term when the previous fire was
    // not confident and this one is, to correct that stale count.
    if (term === lastFiredTerm && (lastFireWasConfident || !confident)) {
      return;
    }
    lastFiredTerm = term;
    lastFireWasConfident = confident;

    trackSearchUsed({
      engine: 'relewise',
      surface: 'predictive',
      query: term,
      resultsCount
    });
  };

  // Debounce with the same interval as the native suggest fetch so per-keystroke
  // re-renders collapse to one fire for the settled term.
  const fireIfNewResultsDebounced = pDebounce(
    fireIfNewResults,
    PREDICTIVE_SEARCH_DEBOUNCE_MS
  );

  // observeDeep attaches to every (possibly late-attached) nested shadow root;
  // a single overlay.shadowRoot observe would miss roots not yet created.
  observeDeep(overlay, fireIfNewResultsDebounced);
};

const registerRelewiseSearchConfig = () => {
  const marketHandle = window.RelewiseConfig?.marketHandle;
  if (marketHandle && !applyRelewiseMarketFilter(marketHandle)) {
    initRelewiseMarketFilter(marketHandle);
  }

  registerRelewiseFacets();
};

if (isRelewiseSearchEnabled()) {
  onRelewiseSearchRegistry(registerRelewiseSearchConfig);
  initRelewiseFallback();
}

const init = () => {
  try {
    if (!initSearchEngine()) {
      return;
    }

    initRelewiseFacets();
  } catch (e) {
    console.error('Error initializing Relewise:', e);
  }
};

// Resilient initialization:
// 1. If loading, wait for DOMContentLoaded
// 2. If already interactive/complete, run init immediately (deferred to next tick to ensure scripts are done)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  setTimeout(init, 0);
}
